// Shape metrics (docs/02-Game-Design/Scoring.md): a constellation is a tree;
// span is its diameter in lines, endpoints the nodes with exactly one line,
// and the shape value min(span, endpoints). The closing line of a ringed
// planet is left out: the metrics are the tapped tree's.
import type { Line } from '../play/types';

export interface ShapeMetrics {
  span: number;
  endpoints: number;
  value: number;
}

export function shapeMetrics(nodes: readonly number[], lines: readonly Line[]): ShapeMetrics {
  if (nodes.length === 0) return { span: 0, endpoints: 0, value: 0 };
  const adj = new Map<number, number[]>();
  for (const id of nodes) adj.set(id, []);
  for (const [a, b] of lines) {
    adj.get(a)?.push(b);
    adj.get(b)?.push(a);
  }
  let endpoints = 0;
  for (const id of nodes) if (adj.get(id)!.length === 1) endpoints++;
  const farthest = (start: number): [number, number] => {
    const depth = new Map<number, number>([[start, 0]]);
    const stack = [start];
    let best = start;
    while (stack.length) {
      const u = stack.pop()!;
      if (depth.get(u)! > depth.get(best)!) best = u;
      for (const v of adj.get(u)!) {
        if (!depth.has(v)) {
          depth.set(v, depth.get(u)! + 1);
          stack.push(v);
        }
      }
    }
    return [best, depth.get(best)!];
  };
  const [end] = farthest(nodes[0]!);
  const [, span] = farthest(end);
  return { span, endpoints, value: Math.min(span, endpoints) };
}

/** Bonus on the letter total by shape value. Starting values, to tune. */
export function shapeBonusPercent(value: number): number {
  if (value >= 12) return 500;
  if (value >= 5) return 100 + (value - 5) * 50;
  switch (value) {
    case 4:
      return 50;
    case 3:
      return 25;
    default:
      return 0;
  }
}
